import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import Text from '../../../components/text/Text';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

const SetupMultiSelector = ({
	items = [],
	onChange,
	SelectorComponent,
	labelExtractor,
	placeholder = 'Add item',
	selectorProps,
	disabled = false,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [pickerKey, setPickerKey] = useState(0);

	const uniqueItems = useMemo(() => {
		const seen = new Set();
		return items.filter(item => {
			if (!item?.id || seen.has(item.id)) {
				return false;
			}
			seen.add(item.id);
			return true;
		});
	}, [items]);

	const handleAdd = useCallback((item) => {
		if (!item?.id) {
			return;
		}
		if (uniqueItems.some(current => current.id === item.id)) {
			setPickerKey(prev => prev + 1);
			return;
		}
		onChange([...uniqueItems, item].map(entry => entry.id));
		setPickerKey(prev => prev + 1);
	}, [uniqueItems, onChange]);

	const handleRemove = useCallback((id) => {
		onChange(uniqueItems.filter(item => item.id !== id).map(item => item.id));
	}, [uniqueItems, onChange]);

	const resolvedLabelExtractor = useCallback((item) => {
		if (typeof labelExtractor === 'function') {
			return labelExtractor(item);
		}
		return item?.name || item?.deviceId || item?.identifier || item?.id;
	}, [labelExtractor]);

	return (
		<View>
			{
				uniqueItems.length > 0 && (
					<View style={styles.chipContainer}>
						{uniqueItems.map(item => (
							<View key={item.id} style={styles.chip}>
								<Text style={styles.chipText}>{resolvedLabelExtractor(item)}</Text>
								<Pressable
									onPress={() => handleRemove(item.id)}
									accessibilityRole="button"
									accessibilityLabel={`Remove ${resolvedLabelExtractor(item)}`}
									style={({ pressed }) => [styles.removeButton, pressed && styles.pressed]}
								>
									<Feather name="x" size={16} color={colors.onChip} />
								</Pressable>
							</View>
						))}
					</View>
				)
			}
			<SelectorComponent
				key={pickerKey}
				onChange={handleAdd}
				disabled={disabled}
				placeholder={placeholder}
				{...(selectorProps || {})}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	chipContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginBottom: space.md,
	},
	chip: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		backgroundColor: colors.chip,
		paddingLeft: space.md,
		borderRadius: radius.pill,
	},
	chipText: {
		...type.secondaryStrong,
		color: colors.onChip,
		flexShrink: 1,
	},
	removeButton: {
		width: touch.min,
		height: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	pressed: {
		opacity: 0.7,
	},
});

export default SetupMultiSelector;
